import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import { IAgent } from "models/Employee/employeeManagement.model";
import { getAgentsAsync } from "redux/actions/employeeManagement.action";

import EmployeePanelItem from "./EmployeePanelItem";
import { Button, Typography } from "components";
import {
  Col,
  ElementWrapper,
  Row,
  Seperator,
} from "components/BaseComponents/Flex";
import { DIGIPOSTHEME } from "theme/digiPosTheme";
import {
  StyledEmpOutWrapper,
  StyledEmpTitleWrapper,
  StyledAddButtonWrapper,
  StyledScrollEmpPanel,
} from "./EmployeePanel.styles";
import { ICONS } from "components/BaseComponents/Image";
import { TokenInclude } from "helpers/GetToken";
import { useMediaQuery } from "context/media/MediaContext";
import { employeePhotoAsync } from "redux/actions/EmployeePhoto.action";
import useLoading from "helpers/hooks/useLoading";

const EmployeePanel: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoading = useLoading();

  let _fromAddOrEditAgent = location.state?.fromAddOrEditAgent || false;

  const { merchantAuthSuccess } = useAppSelector(
    (state) => state.merchantSwitch
  );
  const { user } = useAppSelector((state) => state.login);
  const { employeePhoto } = useAppSelector((state) => state.employeePhoto);
  const { agents } = useAppSelector((state) => state.employeeManagement);
  const token = TokenInclude();

  useEffect(() => {
    if (!employeePhoto?.Avatar) {
      dispatch(employeePhotoAsync(token));
    }
  }, [employeePhoto?.Avatar]);

  useEffect(() => {
    if (!_fromAddOrEditAgent) {
      dispatch(getAgentsAsync(token));
    }
  }, []);

  const filterAgentList = (status: string) => {
    if (agents?.AgentList) {
      return agents.AgentList.filter(
        (agent: IAgent) => agent.Status === status
      ).map((agent: IAgent) => (
        <EmployeePanelItem
          key={agent.MobileNo}
          name={agent.Nickname || `${agent.Name} ${agent.Surname}` || ""}
          phoneNumber={agent.MobileNo || ""}
          status={agent.Status}
          profileImage={agent.ProfileImage}
          isAdmin={false}
        />
      ));
    }
  };

  return (
    <ElementWrapper minHeight='calc(100vh - 137px)' justifyContent='flex-start'>
      <StyledEmpOutWrapper>
        <StyledEmpTitleWrapper>
          <Typography width='' size='16px' color={DIGIPOSTHEME.colors.white}>
            {t("agent_management")}
          </Typography>
        </StyledEmpTitleWrapper>

        <StyledAddButtonWrapper>
          <Row
            backgroundColor={DIGIPOSTHEME.colors.transparent}
            noMargin
            isCursor={true}
            onClick={() => navigate("/add-newAgent")}
            width='auto'
          >
            <Col>
              <Button
                IconSrc={ICONS.USER}
                color='light'
                height={isMobile ? "30px" : "40px"}
                text={t("add_new_agent").toString()}
                type='button'
              />
            </Col>
          </Row>
        </StyledAddButtonWrapper>

        <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
          <Typography
            margin='15px 0'
            size='21px'
            color={DIGIPOSTHEME.colors.darkGrey}
            weight='600'
          >
            {t("my_terminals")}
          </Typography>
        </Row>

        <EmployeePanelItem
          name={user?.adSoyad}
          phoneNumber={
            user?.mbb_phoneNumber || merchantAuthSuccess?.MerchantMobileNo
          }
          profileImage={employeePhoto?.Avatar}
          isAdmin={true}
        />
        {!isLoading && !agents?.AgentList && (
          <Typography
            margin='15px 0 5px 0'
            size='18px'
            color={DIGIPOSTHEME.colors.darkGrey}
            weight='600'
            display={isMobile ? "flex" : "contents"}
          >
            {t("employee_management_empty")}
          </Typography>
        )}
        {filterAgentList("1")?.length! > 0 && (
          <>
            <Row
              backgroundColor={DIGIPOSTHEME.colors.transparent}
              noMargin
              padding='20px 0 0 0'
              width='auto'
            >
              <Typography
                margin='15px 0 5px 0'
                size='20px'
                color={DIGIPOSTHEME.colors.darkGrey}
                weight='600'
                display={isMobile ? "flex" : "contents"}
              >
                {t("active_agents")} ({filterAgentList("1")?.length})
              </Typography>
            </Row>
            <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
              <StyledScrollEmpPanel>
                {filterAgentList("1")}
              </StyledScrollEmpPanel>
            </Row>
          </>
        )}
        {filterAgentList("2")?.length! > 0 && (
          <>
            <Row
              backgroundColor={DIGIPOSTHEME.colors.transparent}
              noMargin
              padding='20px 0 0 0'
            >
              <Typography
                margin='15px 0 5px 0'
                size='20px'
                color={DIGIPOSTHEME.colors.secondaryGraySecondary}
                weight='600'
              >
                {t("inactive_agents")} ({filterAgentList("2")?.length})
              </Typography>
            </Row>
            <Row backgroundColor={DIGIPOSTHEME.colors.transparent} noMargin>
              <StyledScrollEmpPanel maxHeight='450px'>
                {filterAgentList("2")}
              </StyledScrollEmpPanel>
            </Row>
          </>
        )}

        <Seperator />
      </StyledEmpOutWrapper>
    </ElementWrapper>
  );
};

export default EmployeePanel;
